import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { api, ApiError } from "../../lib/api";
import { productsApi, type ProductGoal } from "../../lib/productsApi";
import { ProductMark, type ProductRef } from "../goal/FlowHead";
import { andList } from "../goal/text";
import { useToast } from "../goal/useToast";
import { CX_ORDER, CxRows } from "./CxRows";
import { MailboxOrders } from "./MailboxOrders";
import { useConnections } from "./useConnections";
import { Can, useCan } from "../../context/AuthContext";
import "../../styles/cx.css";

/** What a goal is called in the "Used by" line: its sentence up to "by", and "(ended)" once it is done. */
const goalName = (g: ProductGoal) => `${g.sentence.replace(/\s*\bby\b.*$/i, "")}${g.state === "done" ? " (ended)" : ""}`;

function usedBy(goals: ProductGoal[] | null): string {
  if (!goals) return "";
  if (!goals.length) return "No goal uses it yet";
  const names = goals.map(goalName);
  return `Used by ${names.length > 3 ? `${names.slice(0, 2).join(", ")} and ${names.length - 2} more goals` : andList(names)}`;
}

/** The Connections page's product part (node C1): one product's Meta connection, and cold email on a product that gets it. */
export function ProductConnections() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState<ProductRef[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [goals, setGoals] = useState<{ id: string; goals: ProductGoal[] } | null>(null);
  const can = useCan();
  const { toast, say } = useToast();

  useEffect(() => {
    api.products.list().then(
      (r) => setProducts(r.products.map((p) => ({ id: p.id, name: p.name, logoR2Key: p.logoR2Key, coldEmail: p.coldEmail }))),
      (e) => setLoadError(e instanceof ApiError ? e.message : "The products did not load."),
    );
  }, []);

  const wanted = params.get("product");
  const product = products?.find((p) => p.id === wanted) ?? products?.[0] ?? null;
  const productId = product?.id ?? null;
  const { conns, error, reload } = useConnections(productId);
  const reloadCx = useCallback(() => void reload(), [reload]);

  useEffect(() => {
    if (!productId) return;
    productsApi.goals(productId).then(
      (r) => setGoals({ id: productId, goals: r.goals }),
      () => setGoals({ id: productId, goals: [] }),
    );
  }, [productId]);

  if (loadError) return <p className="load-note">{loadError}</p>;
  if (!products) return null;

  return (
    <div className="ga cxz">
      {products.length === 0 && (
        <section className="card cxp">
          <p className="pd-none">
            Connections belong to a product.{" "}
            <Can min="editor">
              <Link className="ch-lnk" to="/products/new">
                Add a product
              </Link>
            </Can>
          </p>
        </section>
      )}
      {products.length > 1 && (
        <div className="tgls cx-pick" role="group" aria-label="Product">
          {products.map((p) => (
            <button key={p.id} type="button" className={p.id === productId ? "on" : ""} aria-pressed={p.id === productId} onClick={() => setParams({ product: p.id }, { replace: true })}>
              {p.name}
            </button>
          ))}
        </div>
      )}
      {product && (
        <section className="card cxp" id={`cxp-${product.id}`}>
          <div className="card-h">
            <h3 className="cxp-t">
              <ProductMark product={product} />
              <span>{product.name}</span>
            </h3>
            <span className="sub">{usedBy(goals?.id === product.id ? goals.goals : null)}</span>
          </div>
          {error && <p className="cx-err">{error}</p>}
          <div className="rows">
            <CxRows
              key={product.id}
              productId={product.id}
              conns={conns}
              kinds={product.coldEmail ? CX_ORDER : CX_ORDER.filter((k) => k !== "email")}
              reload={reload}
              say={say}
              extra={can("admin") ? { email: <MailboxOrders productId={product.id} productName={product.name} say={say} onReady={reloadCx} /> } : undefined}
            />
          </div>
        </section>
      )}
      {toast}
    </div>
  );
}
